import { useEffect, useMemo, type ElementType } from "react";
import { DataExplorerEditor } from "@/features/data-explorer/components/data-explorer-editor";
import { useEntityIndex } from "@/features/data-explorer/hooks/use-entity-index";
import { useStudioStore } from "@/store/studio-store";
import type { DataCategory } from "@/store/types";
import { Database, GitBranch, Variable, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import { getDataSurfaceCopy } from "@/features/data-explorer/data-surface-copy";
import {
  StudioEditorHost,
  StudioEditorToolbar,
} from "../workspace/studio-editor-host";

// Identity + icon only. The LABEL is locale-derived at render time from
// `data-surface-copy.ts`, which `data-canvas.tsx` reads too — one table, so the
// navigator and the canvas cannot drift into naming the same category
// differently.
const DATA_CATEGORIES: Array<{
  id: DataCategory;
  icon: ElementType;
}> = [
  { id: "variables", icon: Variable },
  { id: "datasources", icon: Database },
  { id: "events", icon: Zap },
  { id: "flows", icon: GitBranch },
];

/**
 * Data surface — IDE v2 inner shell for the Data rail item.
 *
 * Architecture note (Sub-project C PR2): cogo-studio's existing
 * features/data-explorer/ exports DataExplorerEditor — a complete
 * editor with DataCanvas (table/graph view modes, category tabs:
 * variables/datasources/events, search) + DataInspector right panel
 * (opens when an entity is selected).
 *
 * The shared editor host keeps the data category navigator stable while the
 * existing table/graph editor remains the dominant working region.
 */
export function DataSurface({
  visibleCategories = DATA_CATEGORIES.map((category) => category.id),
}: {
  visibleCategories?: readonly DataCategory[];
} = {}) {
  const dataCategory = useStudioStore((s) => s.dataCategory);
  const selectedEntityId = useStudioStore((s) => s.selectedEntityId);
  const { locale } = useStudioLocale();
  const copy = getDataSurfaceCopy(locale);
  const setDataCategory = useStudioStore((s) => s.setDataCategory);
  const selectEntity = useStudioStore((s) => s.selectEntity);
  const index = useEntityIndex();
  const categories = useMemo(
    () =>
      DATA_CATEGORIES.filter((category) =>
        visibleCategories.includes(category.id),
      ),
    [visibleCategories],
  );

  const selectedEntity = useMemo(() => {
    for (const category of categories) {
      const entity = index[category.id].find(
        (candidate) => candidate.id === selectedEntityId,
      );
      if (entity) {
        return {
          id: entity.id,
          name: entity.name,
          blockName: entity.blockName,
          category: category.id,
        };
      }
    }
    return null;
  }, [categories, index, selectedEntityId]);

  useEffect(() => {
    const store = useStudioStore.getState();
    const current = store.chatContext;
    const selectedPropertyKey = selectedEntity?.id ?? dataCategory;
    const dataSourceName =
      selectedEntity?.category === "datasources"
        ? selectedEntity.name
        : undefined;
    if (
      current.surfaceId === "data" &&
      current.selectedPropertyKey === selectedPropertyKey &&
      current.dataSourceName === dataSourceName
    ) {
      return;
    }
    store.setChatContext({
      ...current,
      surfaceId: "data",
      selectedPropertyKey,
      dataSourceName,
    });
  }, [dataCategory, selectedEntity]);

  const dataChatContext = (operation: string) =>
    [
      `Studio data surface operation=${operation}`,
      `Category=${dataCategory}`,
      selectedEntity ? `Selected entity=${selectedEntity.id}` : null,
      "Scope=variables, datasources, events, bindings",
      "Chat operations=add source, connect binding, rename source, debug data flow",
    ]
      .filter(Boolean)
      .join("; ");

  return (
    <StudioEditorHost
      testId="data-editor-host"
      navigatorLabel={copy.dataNavigation}
      navigatorWidth={224}
      toolbar={
        <StudioEditorToolbar
          title={copy.dataTitle}
          subtitle={
            selectedEntity
              ? `${selectedEntity.blockName} / ${selectedEntity.name}`
              : copy.categoryLabel[dataCategory]
          }
          actions={
            <AskCogoSurfaceButton
              label={copy.reviewData}
              operationId="data.surface.review"
              operationKind="data-source"
              chatContextPatch={{
                surfaceId: "data",
                selectedPropertyKey: selectedEntity?.id ?? dataCategory,
                ...(selectedEntity?.category === "datasources"
                  ? { dataSourceName: selectedEntity.name }
                  : {}),
              }}
              context={dataChatContext("review")}
            />
          }
        />
      }
      navigator={
        <div className="flex h-full min-h-0 flex-col overflow-y-auto p-3">
          <section aria-labelledby="data-categories-heading">
            <h3
              id="data-categories-heading"
              className="px-1 text-[11px] font-medium text-muted-foreground"
            >
              {copy.projectData}
            </h3>
            <div className="mt-2 space-y-1">
              {categories.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`data-navigator-${id}`}
                  className={cn(
                    "flex h-8 w-full items-center gap-2 rounded-md px-2 text-xs transition-colors",
                    dataCategory === id
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                  onClick={() => {
                    setDataCategory(id);
                    selectEntity(null);
                  }}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-left">
                    {copy.categoryLabel[id]}
                  </span>
                  <span className="w-6 shrink-0 text-right font-mono text-[11px]">
                    {index.summary[id]}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="mt-5 border-t border-border pt-4">
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {copy.selectedEntity}
            </h3>
            <div className="mt-2 min-h-16 rounded-md border border-border bg-background p-2.5">
              {selectedEntity ? (
                <>
                  <div className="truncate text-xs font-semibold text-foreground">
                    {selectedEntity.name}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                    {selectedEntity.id}
                  </div>
                </>
              ) : (
                <div className="text-xs leading-5 text-muted-foreground">
                  {copy.selectEntityHint}
                </div>
              )}
            </div>
          </section>

          <section
            className="mt-auto border-t border-border pt-4"
            data-testid="data-chat-ops"
          >
            <h3 className="px-1 text-[11px] font-medium text-muted-foreground">
              {copy.chatActions}
            </h3>
            <div className="mt-2 space-y-1.5">
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={copy.addSourceAction}
                operationId="data.source.add"
                operationKind="data-source"
                chatContextPatch={{
                  surfaceId: "data",
                  selectedPropertyKey: "datasources",
                }}
                context={dataChatContext("add-source")}
              />
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={copy.connectAction}
                operationId="data.binding.connect"
                operationKind="data-binding"
                chatContextPatch={{
                  surfaceId: "data",
                  selectedPropertyKey: selectedEntity?.id ?? "data.bindings",
                }}
                context={dataChatContext("connect-binding")}
              />
              <AskCogoSurfaceButton
                className="w-full justify-start"
                label={copy.debugAction}
                operationId="data.flow.debug"
                operationKind="data-source"
                chatContextPatch={{
                  surfaceId: "data",
                  selectedPropertyKey: selectedEntity?.id ?? "data.debug",
                }}
                context={dataChatContext("debug")}
              />
            </div>
          </section>
        </div>
      }
    >
      <div className="h-full min-h-0">
        <DataExplorerEditor
          showCategoryTabs={false}
          visibleCategories={visibleCategories}
        />
      </div>
    </StudioEditorHost>
  );
}
